import React from "react";

import { upperFirst } from "lodash-es";

import type { SelectOption } from "@lib/components/Select";
import { Select } from "@lib/components/Select";

import type {
    CustomSettingImplementation,
    SettingComponentProps,
} from "../../interfacesAndTypes/customSettingImplementation";
import { assertStringArrayOrNull } from "../utils/structureValidation";

import {
    fixupValue,
    isValueValid,
    makeValueConstraintsIntersectionReducerDefinition,
} from "./_shared/arrayMultiSelect";

type ValueType = string[] | null;
type ValueConstraintsType = string[];

export class SelectStringSetting implements CustomSettingImplementation<ValueType, ValueType, ValueConstraintsType> {
    valueConstraintsIntersectionReducerDefinition =
        makeValueConstraintsIntersectionReducerDefinition<ValueConstraintsType>();

    mapInternalToExternalValue(internalValue: ValueType): ValueType {
        return internalValue;
    }

    serializeValue(value: ValueType): string {
        return JSON.stringify(value);
    }

    deserializeValue(serializedValue: string): ValueType {
        const parsed = JSON.parse(serializedValue);
        assertStringArrayOrNull(parsed);
        return parsed;
    }

    isValueValid(currentValue: ValueType, valueConstraints: ValueConstraintsType): boolean {
        return isValueValid<string, string>(currentValue, valueConstraints, (v) => v);
    }

    fixupValue(currentValue: ValueType, valueConstraints: ValueConstraintsType): ValueType {
        return fixupValue<string, string>(currentValue, valueConstraints, (v) => v, "firstAvailable");
    }

    makeComponent(): (props: SettingComponentProps<ValueType, ValueConstraintsType>) => React.ReactNode {
        return function SelectStringSetting(props: SettingComponentProps<ValueType, ValueConstraintsType>) {
            const options: SelectOption[] = React.useMemo(() => {
                const availableValues = props.valueConstraints ?? [];
                return availableValues.map((stringVals) => ({
                    value: stringVals,
                    label: upperFirst(stringVals),
                }));
            }, [props.valueConstraints]);

            function handleChange(selectedUuids: string[]) {
                props.onValueChange(selectedUuids);
            }

            return (
                <Select
                    filter
                    options={options}
                    value={props.value ?? undefined}
                    onValueChange={handleChange}
                    showQuickSelectButtons={true}
                    disabled={props.disabled}
                    multiple={true}
                    size={5}
                />
            );
        };
    }
}
